/* eslint-disable complexity */
/* eslint-disable class-methods-use-this */

// eslint-disable-next-line no-unused-vars
import React, {
    useState, useEffect, useRef, useCallback, useMemo
} from 'react';
import ReactDOM from 'react-dom';
import AddressActions from 'actions/AddressActions';
import LanguageLocale from 'utils/LanguageLocale';
import utilityApi from 'services/api/utility';
import store from 'store/Store';
import ErrorsUtils from 'utils/Errors';
import ErrorConstants from 'utils/ErrorConstants';
import HelperUtils from 'utils/Helpers';
import FormValidator from 'utils/FormValidator';
import Debounce from 'utils/Debounce';
import keyConsts from 'utils/KeyConstants';
import addressUtils from 'utils/Address';
import helperUtils from 'utils/Helpers';
import ErrorList from 'components/ErrorList';
import { colors, forms, space } from 'style/config';
import { mediaQueries } from 'style/config';
import FrictionlessUtils from 'utils/FrictionlessCheckout';

import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Grid } from 'components/ui';
import NameFields from 'components/Addresses/FrictionlessAddressForm/components/NameFields';
import CountrySelect from 'components/Addresses/FrictionlessAddressForm/components/CountrySelect';
import PhoneField from 'components/Addresses/FrictionlessAddressForm/components/PhoneField';
import EmailField from 'components/Addresses/FrictionlessAddressForm/components/EmailField';
import FirstAddressField from 'components/Addresses/FrictionlessAddressForm/components/FirstAddressField';
import SecondAddressField from 'components/Addresses/FrictionlessAddressForm/components/SecondAddressField';
import Avs from 'components/Addresses/FrictionlessAddressForm/components/Avs';
import PostalCodeField from 'components/Addresses/FrictionlessAddressForm/components/PostalCodeField';
import CityField from 'components/Addresses/FrictionlessAddressForm/components/CityField';
import MultiCityZipDropdown from 'components/Addresses/FrictionlessAddressForm/components/MultiCityZipDropdown';
import StateProvinceField from 'components/Addresses/FrictionlessAddressForm/components/StateProvinceField';
import { useLoqateAddresses } from 'hooks/useLoqateAddresses';
import { useCountryStateManagement } from 'hooks/useCountryStateManagement';

const DEBOUNCE_ADDRESS = 200;
let debounceSubscription;
const GRID_GUTTER = 4;
const { isCountryInternational, isValidCountry, COUNTRIES } = LanguageLocale;
const {
    sanitizeUSAddress, sanitizeUSAddressTwo, sanitizeInput, sanitizeAvsAddress, sanitizeAvsField
} = helperUtils;

const countyInitialData = (address, country) => {
    const international = isCountryInternational(country || COUNTRIES.US);
    const rawPhone = address?.phone || '';
    const formattedPhone = rawPhone ? (international ? rawPhone : FormValidator.getFormattedPhoneNumber(rawPhone)) : '';

    return {
        ...address,
        country: country || COUNTRIES.US,
        formattedPhone: formattedPhone
    };
};

const isFCFinalReleaseEnabled = FrictionlessUtils.isFCFinalReleaseEnabled();

function FrictionlessAddressForm(props) {
    const [address, setAddress] = useState(() => countyInitialData(props.address, props.country));
    const [errorMessages] = useState('');
    const [showAddress2Input, setShowAddress2Input] = useState(
        props.showAddress2Input ? props.showAddress2Input : props.address && props.address.address2
    );
    const [hasAddress1Focus, setHasAddress1Focus] = useState(false);
    const [isAddressVerified, setIsAddressVerified] = useState(props.address && props.address.isAddressVerified);
    const [isAddressModified, setIsAddressModified] = useState(false);
    const [isAdsRestricted] = useState(props.isAdsRestricted);
    const [isInternational, setIsInternational] = useState(false);
    const [hasLoqateError, setHasLoqateError] = useState(false);
    // AVS Loqate v2 (GIM-5453): a ZIP serving several cities forces a dropdown choice; a
    // single-city ZIP auto-fills + locks city/state read-only until the ZIP changes.
    const [multiCityOptions, setMultiCityOptions] = useState([]);
    const [isCityStateLockedByZip, setIsCityStateLockedByZip] = useState(false);

    const firstNameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const addressInputRef = useRef(null);
    const address2InputRef = useRef(null);
    const zipCodeInputRef = useRef(null);
    const cityInputRef = useRef(null);
    const stateSelectRef = useRef(null);
    const regionInputRef = useRef(null);
    const phoneNumberInputRef = useRef(null);
    const emailInputRef = useRef(null);
    const countrySelectRef = useRef(null);
    const addressPredictionRef = useRef(null);
    // Tracks whether address1 input has focus so we can skip Redux-driven state sync while user is typing
    const address1IsFocusedRef = useRef(false);
    const loqateWaitingForRef = useRef('');
    // GIM-5453: monotonic token so an out-of-order ZIP lookup response cannot overwrite the
    // multi-city / lock state produced by a newer ZIP (durable state, unlike the legacy transient race).
    const zipLookupSeqRef = useRef(0);

    const avsLoqateV2 = Sephora.configurationSettings?.avsLoqateV2;

    const hasAVS = useCallback(() => {
        const { isBillingAddress, hasAVS: hasAVSProp = true } = props;

        return Sephora.configurationSettings.enableAddressValidation && !isInternational && !isBillingAddress && hasAVSProp;
    }, [props.isBillingAddress, props.hasAVS, isInternational]);

    const updateEditStore = useCallback(
        (name, value) => {
            const editStore = store.getState().editData[props.editStore];
            props.updateEditData(Object.assign({}, editStore, { [name]: value }), props.editStore);

            hasAVS() && verifyLoqateAddressState(name, value);
        },
        [props.editStore, hasAVS]
    );

    const {
        stateList,
        countryInvalid,
        setStateListData,
        displayCityStateInputs,
        zipCodeInvalid,
        setDisplayCityStateInputs,
        setZipCodeInvalid,
        cityStateZipInvalid,
        setCityStateZipInvalid,
        handleCityOnChange,
        handleStateSelect,
        handleCountrySelect
    } = useCountryStateManagement({
        isEditMode: props.isEditMode,
        updateEditStore,
        onCountryChange: props.onCountryChange,
        state: address.state,
        setAddress,
        countrySelectRef,
        stateSelectRef,
        displayCityStateInputsprop: props.address && props.address.postalCode,
        setIsInternational,
        avsLoqateV2,
        selectedCountry: address.country
    });

    const updateLoqateVerifiedAddressState = useCallback(
        ({
            addressLine1, addressLine2, city, postalCode, stateLocal, isPOBoxAddress, addressType
        }) => {
            address1IsFocusedRef.current = false;
            setAddress(prev => ({ ...prev, address1: addressLine1 }));

            updateEditStore('address1', addressLine1);
            updateEditStore('address2', addressLine2);

            if (addressLine2) {
                setShowAddress2Input(true);
            }

            updateEditStore('city', city);
            updateEditStore('postalCode', postalCode);
            updateEditStore('state', stateLocal);
            updateEditStore('isPOBoxAddress', isPOBoxAddress);
            updateEditStore('addressType', addressType);

            setDisplayCityStateInputs(true);
            setIsAddressVerified(true);
            setZipCodeInvalid(false);
            setCityStateZipInvalid(false);

            // Clear any Loqate selection error when user selects from dropdown
            setHasLoqateError(false);
            addressInputRef.current?.showError(null);

            props.onSubmitLoqateAddress();
        },
        [
            updateEditStore,
            props.onSubmitLoqateAddress,
            setShowAddress2Input,
            setDisplayCityStateInputs,
            setIsAddressVerified,
            setZipCodeInvalid,
            setCityStateZipInvalid
        ]
    );

    const setFieldFocus = useCallback(node => {
        clearTimeout(debounceSubscription);
        ReactDOM.findDOMNode(node).querySelector('input').focus();
    }, []);

    const blurAddress1 = useCallback(() => {
        address1IsFocusedRef.current = false;
        setHasAddress1Focus(false);
        setLoqateAddresses([]);
    }, [address.address1]);

    const handleAddress1Blur = useCallback(() => {
        debounceSubscription = Debounce.debounce.call(this, blurAddress1, DEBOUNCE_ADDRESS)();
    }, [blurAddress1]);

    const section = props.isGiftCardSection ? 'gift card' : 'deliver to';

    const {
        loqateAddresses,
        highlightedAddrIndex,
        getLoqateAddresses,
        setLoqateAddresses,
        setHighlightedAddrIndex,
        handleLoqateAddressClick,
        getHighlight
    } = useLoqateAddresses({
        address,
        loqateWaitingForRef,
        addressPredictionRef,
        updateLoqateVerifiedAddressState,
        setFieldFocus,
        handleAddress1Blur,
        addressInputRef,
        section
    });

    const renderAVS = useCallback(() => {
        return (
            <Avs
                loqateAddresses={loqateAddresses}
                hasAddress1Focus={hasAddress1Focus}
                highlightedAddrIndex={highlightedAddrIndex}
                localization={props.localization}
                onMouseLeaveList={() => setHighlightedAddrIndex(-1)}
                onMouseEnterItem={setHighlightedAddrIndex}
                onClickItem={handleLoqateAddressClick}
                getHighlight={getHighlight}
                addressPredictionRef={comp => (addressPredictionRef.current = comp)}
            />
        );
    }, [loqateAddresses, hasAddress1Focus, highlightedAddrIndex, props.localization]);

    const componentWillUnmount = useCallback(() => {
        props.clearEditData(props.editStore);
        clearTimeout(debounceSubscription);
    }, [props.editStore]);

    const formatZipPostalCode = useCallback(
        (inputData, divider, leftLength, totalLength) => {
            const formatZipPostal = addressUtils.formatZipPostalCode(inputData, divider, leftLength, totalLength);
            updateEditStore('postalCode', formatZipPostal);

            return formatZipPostal;
        },
        [updateEditStore]
    );

    const autoFillCityAndState = useCallback(
        zipCode => {
            // Capture this lookup's sequence number; a later ZIP change bumps the ref, so a stale
            // response (resolving after a newer request) is ignored below.
            const seq = ++zipLookupSeqRef.current;

            utilityApi
                // GIM-5588: forward the already-entered street/state so the service can narrow the
                // multi-city list. Harmless when the flag is off (getStateAndCityForZipCode ignores the
                // 3rd arg on the legacy path) or when SDN (GIM-5462) does not yet relay them.
                .getStateAndCityForZipCode(address.country, zipCode, {
                    address1: address.address1,
                    address2: address.address2,
                    state: address.state
                })
                .then(data => {
                    if (seq !== zipLookupSeqRef.current) {
                        return;
                    }

                    setDisplayCityStateInputs(true);
                    setZipCodeInvalid(null);
                    // Clear any stale server LOOKUP_*_INVALID error (handleResponseError) now that
                    // city/state are being re-derived from a valid ZIP.
                    setCityStateZipInvalid(null);

                    // AVS Loqate v2: a ZIP serving more than one city must NOT auto-fill — the shopper
                    // picks from a dropdown. Clear any prior city/state and surface the list.
                    if (avsLoqateV2 && data.isMultiCity) {
                        setMultiCityOptions(data.cities || []);
                        setIsCityStateLockedByZip(false);
                        // Clear BOTH city and state: a prior ZIP may have filled a now-stale state that
                        // must not remain visible/validated until the shopper picks a city.
                        updateEditStore('city', '');
                        updateEditStore('state', '');

                        return;
                    }

                    setMultiCityOptions([]);
                    // A single, unambiguous city/state from the ZIP locks both fields read-only until
                    // the ZIP changes (formal AC wins over §3.2 prose).
                    setIsCityStateLockedByZip(!!avsLoqateV2);
                    updateEditStore('state', data.state);
                    updateEditStore('city', HelperUtils.titleCase(data.city));
                })
                .catch(() => {
                    if (seq !== zipLookupSeqRef.current) {
                        return;
                    }

                    updateEditStore('state', '');
                    updateEditStore('city', '');
                    setDisplayCityStateInputs(false);
                    setMultiCityOptions([]);
                    setIsCityStateLockedByZip(false);
                    // Reset any stale server city/state error so it can't linger after the ZIP failed
                    // and the fields were hidden.
                    setCityStateZipInvalid(null);
                    setZipCodeInvalid('Please enter a valid zipcode.');
                });
        },
        [address.country, address.address1, address.address2, address.state, updateEditStore, avsLoqateV2]
    );

    const handleMultiCitySelect = useCallback(
        ({ city, state }) => {
            updateEditStore('city', HelperUtils.titleCase(city));
            updateEditStore('state', state);
            setMultiCityOptions([]);
            // once a city is chosen for the ZIP, city/state lock until the ZIP changes
            setIsCityStateLockedByZip(true);
        },
        [updateEditStore]
    );

    const handleCountryChange = useCallback(
        e => {
            // GIM-5453: a country change must drop any prior ZIP-derived multi-city state and
            // invalidate an in-flight lookup — otherwise stale US/CA cities can render for the new
            // country (e.g. the dropdown on an international address) or a late response can re-fill/
            // lock for the wrong country. resetCountryAndStateInfo (handleCountrySelect) resets
            // country/state/city but not this form-local state, so wrap it here.
            setMultiCityOptions([]);
            setIsCityStateLockedByZip(false);
            zipLookupSeqRef.current += 1;
            handleCountrySelect(e);
        },
        [handleCountrySelect]
    );

    const handleZipCodeOnChange = useCallback(
        e => {
            // GIM-5454: strip ZIP/postal to alphanumeric + space + hyphen on input when AVS v2 is on.
            // Legacy path keeps the raw value; the per-country formatter below still applies in both.
            const value =
                avsLoqateV2 && isValidCountry(address.country) ? sanitizeAvsField('postalCode', e.target.value, address.country) : e.target.value;
            const currentPostalCode = address.postalCode;

            if (value === currentPostalCode) {
                return;
            }

            // GIM-5453 race guard: the ZIP is changing, so invalidate any in-flight city lookup up
            // front. This covers the clear/shorten branches below (which reset the UI but start no new
            // lookup) — without it, a late/out-of-order response could re-fill and re-lock city/state
            // for a ZIP the shopper already cleared. autoFillCityAndState bumps again when it fires, so
            // only the newest lookup's response is applied.
            zipLookupSeqRef.current += 1;

            if (address.country === COUNTRIES.US) {
                const zipCode = formatZipPostalCode(value, '-', 5, 9);

                if (zipCode && !zipCodeInvalid && (zipCode.length === 5 || zipCode.length === 10) && !address.state && !address.city) {
                    autoFillCityAndState(zipCode);
                } else if (!zipCode || (zipCode && zipCode.length < 5)) {
                    if (address.state || address.city) {
                        updateEditStore('state', '');
                        updateEditStore('city', '');
                    }

                    setZipCodeInvalid(null);
                    setCityStateZipInvalid(null);
                    setDisplayCityStateInputs(false);
                    setMultiCityOptions([]);
                    setIsCityStateLockedByZip(false);
                } else if (zipCode && zipCode.length === 5 && zipCode !== currentPostalCode.slice(0, 5)) {
                    autoFillCityAndState(zipCode);
                }
            } else if (address.country === COUNTRIES.CA) {
                const postalCode = formatZipPostalCode(value.toUpperCase(), ' ', 3, 6);

                if (postalCode && postalCode.length === 7) {
                    autoFillCityAndState(postalCode);
                } else if (!postalCode || (postalCode && postalCode.length < 7)) {
                    if (address.state || address.city) {
                        updateEditStore('state', '');
                        updateEditStore('city', '');
                    }

                    setZipCodeInvalid(null);
                    setCityStateZipInvalid(null);
                    setDisplayCityStateInputs(false);
                    setMultiCityOptions([]);
                    setIsCityStateLockedByZip(false);
                }
            } else {
                updateEditStore('postalCode', value.toUpperCase());
            }
        },
        [address, zipCodeInvalid, formatZipPostalCode, autoFillCityAndState, updateEditStore, avsLoqateV2]
    );

    const getFormattedPhoneNumber = useCallback(
        rawValue => {
            if (isInternational) {
                return rawValue;
            }

            return FormValidator.getFormattedPhoneNumber(rawValue);
        },
        [isInternational]
    );

    const handlePhoneChange = useCallback(
        e => {
            const isPhoneAutofilled = phoneNumberInputRef.current?.inputElementRef?.matches(':-webkit-autofill');
            const currentValue = phoneNumberInputRef.current?.inputElementRef?.value || '';
            const rawPhone = currentValue.replace(HelperUtils.specialCharacterRegex, '');
            const formattedPhone = getFormattedPhoneNumber(rawPhone);
            updateEditStore('phone', rawPhone);
            updateEditStore('formattedPhone', formattedPhone);

            if (isPhoneAutofilled) {
                requestAnimationFrame(() => {
                    setAddress(prev => ({
                        ...prev,
                        phone: rawPhone,
                        formattedPhone: formattedPhone
                    }));

                    // Force the input to show the formatted value
                    if (phoneNumberInputRef.current?.setValue) {
                        phoneNumberInputRef.current.setValue(formattedPhone);
                    }
                });

                return;
            }

            formatPhoneNumber(e);
        },
        [getFormattedPhoneNumber, updateEditStore]
    );

    const formatPhoneNumber = useCallback(
        e => {
            const rawValue = e.target.value.replace(HelperUtils.specialCharacterRegex, '');
            const formattedPhone = getFormattedPhoneNumber(rawValue);

            setAddress(prev => ({
                ...prev,
                phone: rawValue,
                formattedPhone: formattedPhone
            }));
        },
        [getFormattedPhoneNumber]
    );

    useEffect(() => {
        if (!phoneNumberInputRef.current?.inputElementRef) {
            return;
        }

        // Use requestAnimationFrame to defer cursor positioning until after React's batched updates
        requestAnimationFrame(() => {
            const inputElement = phoneNumberInputRef.current?.inputElementRef;

            if (!inputElement) {
                return;
            }

            // Only manipulate cursor if this input is currently focused
            // This prevents issues when multiple forms are present (e.g., Gift Card + merch)
            if (document.activeElement !== inputElement) {
                return;
            }

            let additionalSpacesAddedByFormatter = 0;
            const currentCursorPosition = inputElement.selectionStart;

            if (address?.phone?.length === 4) {
                additionalSpacesAddedByFormatter = 3;
            } else if (address?.phone?.length === 7) {
                additionalSpacesAddedByFormatter = 1;
            }

            const newPosition = currentCursorPosition + additionalSpacesAddedByFormatter;
            // Use setSelectionRange instead of setting selectionEnd directly for better iOS compatibility
            inputElement.setSelectionRange(newPosition, newPosition);
        });
    }, [address?.phone, address?.formatPhoneNumber]);

    const validateForm = useCallback(
        (doNotClearErrors, shouldDispatchErrors = true) => {
            const fieldsForValidation = [];

            // if user is on checkout and is editing a billing address
            // there is no firstName or lastName inputs, so no need to validate
            if (props.isBillingAddress && props.isCheckout) {
                fieldsForValidation.push(addressInputRef.current, zipCodeInputRef.current, cityInputRef.current);
            } else {
                fieldsForValidation.push(
                    firstNameInputRef.current,
                    lastNameInputRef.current,
                    addressInputRef.current,
                    zipCodeInputRef.current,
                    cityInputRef.current
                );

                if (props.isGuestCheckout) {
                    fieldsForValidation.push(emailInputRef.current);
                }
            }

            if (isInternational) {
                fieldsForValidation.push(regionInputRef.current);
            }

            if (!props.isPhoneFieldHidden) {
                fieldsForValidation.push(phoneNumberInputRef.current);
            }

            if (!doNotClearErrors) {
                ErrorsUtils.clearErrors();
            }

            ErrorsUtils.collectClientFieldErrors(fieldsForValidation);

            const isFieldsValid = !ErrorsUtils.validate(fieldsForValidation, shouldDispatchErrors);

            // Check if Loqate selection is required but not made
            let loqateError = false;

            if (props.isShortenedLoqateForm && !isAddressVerified && address.address1) {
                // User typed in shortened Loqate form but didn't select from dropdown
                loqateError = true;
                setHasLoqateError(true);

                if (shouldDispatchErrors) {
                    // Show error with minimal message for accessibility
                    const errorMessage = props.loqateSelectionError || '';
                    addressInputRef.current?.showError(errorMessage);
                }
            } else {
                setHasLoqateError(false);
            }

            return isFieldsValid && !loqateError;
        },
        [props, isInternational, isAddressVerified, address.address1]
    );

    const handleResponseError = useCallback((message, value, errorKey) => {
        const cityInvalid = errorKey === ErrorConstants.ERROR_CODES.LOOKUP_CITY_INVALID && message;
        const stateInvalid = errorKey === ErrorConstants.ERROR_CODES.LOOKUP_STATE_INVALID && message;
        const zipCodeInvalidValue = errorKey === ErrorConstants.ERROR_CODES.LOOKUP_POSTAL_CODE_INVALID && message;
        const locationError = cityInvalid || stateInvalid || zipCodeInvalidValue;

        if (locationError) {
            setCityStateZipInvalid(locationError);

            return true; // returns true if any of these errors are handled by this form
        }

        switch (errorKey) {
            case ErrorConstants.ERROR_CODES.ADDRESS1_INCORRECT:
                addressInputRef.current.showError(message, value, errorKey);

                return true;
            case ErrorConstants.ERROR_CODES.ADDRESS2_INCORRECT:
                address2InputRef.current.showError(message, value, errorKey);

                return true;
            case ErrorConstants.ERROR_CODES.FIRST_NAME_INCORRECT:
                firstNameInputRef.current.showError(message, value, errorKey);

                return true;
            case ErrorConstants.ERROR_CODES.LAST_NAME_INCORRECT:
                lastNameInputRef.current.showError(message, value, errorKey);

                return true;
            default:
        }

        return false;
    }, []);

    const getData = useCallback(() => {
        if (address.country === COUNTRIES.CA) {
            address.postalCode.toUpperCase();
        }

        const addressData = {
            address: {
                firstName: address.firstName,
                lastName: address.lastName,
                address1: address.address1,
                address2: address.address2,
                postalCode: address.postalCode,
                city: address.city,
                state: address.state,
                country: address.country
            },
            isPOBoxAddress: isAddressVerified ? address.isPOBoxAddress : undefined,
            addressType: isAddressVerified ? address.addressType : undefined
        };

        if (!props.isPhoneFieldHidden) {
            addressData.address.phone = address.phone.replace(HelperUtils.specialCharacterRegex, '');
        }

        if (props.isGuestCheckout) {
            addressData.address.shipEmail = address.email;
        }

        if (props.isEditMode) {
            addressData.address.addressId = address.addressId;
        }

        return addressData;
    }, [address, isAddressVerified, props]);

    const handleAddress2Change = useCallback(
        e => {
            const sanitizedValue =
                avsLoqateV2 && isValidCountry(address.country)
                    ? sanitizeAvsField('address2', e.target.value, address.country)
                    : sanitizeUSAddressTwo(e.target.value);
            address2InputRef.current?.setValue(sanitizedValue);
            updateEditStore(e.target.name, sanitizedValue);
        },
        [updateEditStore, avsLoqateV2, address.country]
    );

    const handleAddress2SectionClick = useCallback(() => {
        setShowAddress2Input(true);
        setFieldFocus(address2InputRef.current);
    }, [setFieldFocus]);

    const handleAddress1Focus = useCallback(() => {
        address1IsFocusedRef.current = true;
        addressInputRef.current?.setValue(address.address1);
        setHasAddress1Focus(true);
    }, [address.address1]);

    // Check if we make the Loqate Address dirty by manually modyfing addresses fields

    const verifyLoqateAddressState = useCallback(
        (field, value) => {
            let initialFieldValue;

            switch (field) {
                case 'address1':
                case 'address2':
                case 'postalCode':
                case 'city':
                case 'state':
                    initialFieldValue = props.address && props.address[field];

                    if (initialFieldValue !== value) {
                        setIsAddressVerified(false);
                        setIsAddressModified(true);
                    }

                    break;
                default:
                    break;
            }
        },
        [props.address]
    );

    const handleAddress1Change = useCallback(
        value => {
            const sanitizedValue =
                avsLoqateV2 && isValidCountry(address.country) ? sanitizeAvsAddress(value, address.country) : sanitizeUSAddress(value);
            loqateWaitingForRef.current = sanitizedValue;

            if (!props.shouldHiddenLoqate) {
                getLoqateAddresses(sanitizedValue);
            }
        },
        [props.shouldHiddenLoqate, getLoqateAddresses, avsLoqateV2, address.country]
    );

    const handleAddress1ChangeDebounced = useCallback(
        e => Debounce.debounce(handleAddress1Change(e.target.value), DEBOUNCE_ADDRESS),
        [handleAddress1Change]
    );

    const handleAddress1KeyUp = useCallback(
        e => {
            const value =
                avsLoqateV2 && isValidCountry(address.country)
                    ? sanitizeAvsAddress(e.target.value, address.country)
                    : sanitizeUSAddress(e.target.value);
            const fieldName = e.target.name;
            addressInputRef.current?.setValue(value);

            // Keep local state in sync so the controlled input never resets to a stale value on re-render
            setAddress(prev => ({ ...prev, address1: value }));
            updateEditStore(fieldName, value);

            // return early if AVS shouldn't be displayed
            if (!hasAVS()) {
                return;
            }

            switch (e.key) {
                case keyConsts.UP:
                case keyConsts.DOWN:
                case keyConsts.ENTER:
                    e.preventDefault();

                    return;
                case keyConsts.ESC:
                    e.preventDefault();
                    getLoqateAddresses(value);

                    return;
                default:
                    return;
            }
        },
        [updateEditStore, hasAVS, getLoqateAddresses, setAddress, avsLoqateV2, address.country]
    );

    const handleFnameBlur = useCallback(() => {
        const rawValue = firstNameInputRef.current?.getValue();
        const value =
            avsLoqateV2 && isValidCountry(address.country) ? sanitizeAvsField('firstName', rawValue, address.country) : sanitizeInput(rawValue);
        firstNameInputRef.current?.setValue(value);
    }, [avsLoqateV2, address.country]);

    const handleLnameBlur = useCallback(() => {
        const rawValue = lastNameInputRef.current?.getValue();
        const value =
            avsLoqateV2 && isValidCountry(address.country) ? sanitizeAvsField('lastName', rawValue, address.country) : sanitizeInput(rawValue);

        lastNameInputRef.current?.setValue(value);
    }, [avsLoqateV2, address.country]);

    const handleAddress1KeyDown = useCallback(
        e => {
            let index = highlightedAddrIndex;

            switch (e.key) {
                case keyConsts.TAB:
                    return;
                case keyConsts.ESC:
                    loqateWaitingForRef.current = '';
                    e.target.value = '';

                    return;
                case keyConsts.UP:
                    if (index <= 0) {
                        index = loqateAddresses.length - 1;
                    } else {
                        index--;
                    }

                    break;
                case keyConsts.DOWN:
                    if (index === -1 || index >= loqateAddresses.length - 1) {
                        index = 0;
                    } else {
                        index++;
                    }

                    break;
                case keyConsts.ENTER:
                    if (loqateAddresses[index]) {
                        e.preventDefault();
                        handleLoqateAddressClick(loqateAddresses[index]);
                    }

                    return;
                default:
                    !hasAddress1Focus && handleAddress1Focus();

                    return;
            }

            e.preventDefault();
            !hasAddress1Focus && handleAddress1Focus();
            setHighlightedAddrIndex(index, true);
        },
        [highlightedAddrIndex, loqateAddresses, hasAddress1Focus, handleLoqateAddressClick, handleAddress1Focus, setHighlightedAddrIndex]
    );

    const isAddressVerifiedFunc = useCallback(() => {
        return isAddressVerified;
    }, [isAddressVerified]);

    const isAddressModifiedFunc = useCallback(() => {
        return isAddressModified;
    }, [isAddressModified]);

    const getHasLoqateError = useCallback(() => {
        return hasLoqateError;
    }, [hasLoqateError]);

    useEffect(() => {
        //TODO. this is a trick. once we have react 18+ we can use useImperativeHandle  --- and forwardRef
        props.onRef({
            validateForm,
            getData,
            hasAVS,
            isAddressVerified: isAddressVerifiedFunc,
            isAddressModified: isAddressModifiedFunc,
            hasLoqateError: getHasLoqateError,
            handleResponseError,
            firstNameInput: firstNameInputRef.current,
            lastNameInput: lastNameInputRef.current,
            addressInput: addressInputRef.current,
            address2Input: address2InputRef.current,
            zipCodeInput: zipCodeInputRef.current,
            cityInput: cityInputRef.current,
            stateSelect: stateSelectRef.current,
            regionInput: regionInputRef.current,
            phoneNumberInput: phoneNumberInputRef.current,
            emailInput: emailInputRef.current
        });
    }, [validateForm, getData, hasAVS, isAddressVerifiedFunc, isAddressModifiedFunc, getHasLoqateError, handleResponseError]);

    useEffect(() => {
        setAddress(prev => {
            const update = { ...props.editStoreData };

            // Skip Redux sync while user is typing address1 to avoid dropping characters
            if (address1IsFocusedRef.current) {
                delete update.address1;
            }

            return { ...prev, ...update };
        });
    }, [props.editStoreData]);

    useEffect(() => {
        loqateWaitingForRef.current = '';

        props.isCheckout && firstNameInputRef.current && firstNameInputRef.current.focus();

        if (!props.isEditMode && props.biAccount) {
            const { firstName, lastName, homePhone } = props.biAccount;
            setAddress(prev => ({
                ...prev,
                firstName: firstName,
                lastName: lastName,
                phone: homePhone || '',
                formattedPhone: getFormattedPhoneNumber(homePhone || '')
            }));
        }

        return componentWillUnmount;
    }, [setAddress, props.isEditMode, props.biAccount, componentWillUnmount, getFormattedPhoneNumber]);

    useEffect(() => {
        // Initialize edit store with address prop on mount
        const country = address.country;

        if (isInternational && props.isEditMode) {
            updateEditStore('state', props.address.state);
        } else if (!isInternational) {
            AddressActions.getStateList(country, states => setStateListData(states, country));
        }
    }, [updateEditStore, props.address?.state, address?.country]);

    const {
        isPhoneFieldHidden,
        isBillingAddress,
        isCheckout,
        isCountryFieldHidden,
        isShortenedLoqateForm,
        isFirstNameFieldDisabled,
        isLastNameFieldDisabled,
        fireFormErrorAnalytics,
        modalTitle,
        isSubscription,
        disableCountryList,
        isCountryFieldLast = false,
        forceSingleColumn = false
    } = props;

    const {
        localization: { enterZipCodeText }
    } = props;

    const isUS = address.country === COUNTRIES.US;

    // GIM-5452 (E2-3): mark exactly the inputs the AVS service flagged via highlightedFields[].
    // Server field names -> input keys mapping is centralized in addressUtils.getHighlightedInputs.
    // Memoized per highlightedFields reference so the Set is built once per render (it is queried by
    // every field via .has()), consistent with the class-form variants.
    const highlightedInputs = useMemo(() => addressUtils.getHighlightedInputs(props.highlightedFields), [props.highlightedFields]);

    return (
        <div>
            <ErrorList errorMessages={errorMessages} />
            {isCheckout && isBillingAddress ? null : (
                <NameFields
                    firstName={address.firstName}
                    lastName={address.lastName}
                    isFirstNameFieldDisabled={isFirstNameFieldDisabled}
                    isLastNameFieldDisabled={isLastNameFieldDisabled}
                    firstNameHighlighted={highlightedInputs.has('firstName')}
                    lastNameHighlighted={highlightedInputs.has('lastName')}
                    isAdsRestricted={isAdsRestricted}
                    isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                    isSubscription={isSubscription}
                    modalTitle={modalTitle}
                    localization={props.localization}
                    avsLoqateV2={avsLoqateV2}
                    country={address.country}
                    onFirstNameChange={updateEditStore}
                    onLastNameChange={updateEditStore}
                    onFirstNameBlur={handleFnameBlur}
                    onLastNameBlur={handleLnameBlur}
                    firstNameRef={comp => (firstNameInputRef.current = comp)}
                    lastNameRef={comp => (lastNameInputRef.current = comp)}
                    fireFormErrorAnalytics={fireFormErrorAnalytics}
                    forceSingleColumn={forceSingleColumn}
                />
            )}
            <Grid
                gap={[0, GRID_GUTTER]}
                gridTemplateColumns={forceSingleColumn ? '1fr' : ['1fr 1fr']}
            >
                {isCountryFieldHidden || isCountryFieldLast || (
                    <div css={styles.fullField}>
                        <CountrySelect
                            country={address.country}
                            countryInvalid={countryInvalid}
                            countryList={props.countryList}
                            isBillingAddress={isBillingAddress}
                            disableCountryList={disableCountryList}
                            localization={props.localization}
                            onChange={handleCountrySelect}
                            countrySelectRef={comp => (countrySelectRef.current = comp)}
                            isUS={isUS}
                        />
                    </div>
                )}
                {isPhoneFieldHidden || (
                    <div css={styles.fullField}>
                        <PhoneField
                            phone={address.phone}
                            formattedPhone={address.formattedPhone}
                            isInternational={isInternational}
                            phoneInvalid={false} // Note: phoneInvalid was not used in original, assuming false
                            isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                            isSubscription={isSubscription}
                            modalTitle={modalTitle}
                            highlighted={highlightedInputs.has('phone')}
                            localization={props.localization}
                            onChange={isFCFinalReleaseEnabled ? handlePhoneChange : formatPhoneNumber}
                            phoneNumberRef={comp => (phoneNumberInputRef.current = comp)}
                            fireFormErrorAnalytics={fireFormErrorAnalytics}
                        />
                    </div>
                )}
                {props.shouldDisplayEmailInputField && (
                    <div css={styles.fullField}>
                        <EmailField
                            email={address.email}
                            isAdsRestricted={isAdsRestricted}
                            isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                            localization={props.localization}
                            onChange={e => updateEditStore(e.target.name, e.target.value)}
                            emailInputRef={comp => (emailInputRef.current = comp)}
                        />
                    </div>
                )}
            </Grid>
            <Grid gridTemplateColumns={forceSingleColumn ? '1fr' : [null, '1fr 1fr']}>
                <FirstAddressField
                    address1={address.address1}
                    isAdsRestricted={isAdsRestricted}
                    isAddressVerified={isAddressVerified}
                    hasAVS={hasAVS()}
                    isShortenedLoqateForm={isShortenedLoqateForm}
                    avsLoqateV2={avsLoqateV2}
                    country={address.country}
                    highlightedAddrIndex={highlightedAddrIndex}
                    hasAddress1Focus={hasAddress1Focus}
                    hasLoqateError={hasLoqateError}
                    highlighted={highlightedInputs.has('address1')}
                    shouldHiddenLoqate={props.shouldHiddenLoqate}
                    isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                    isSubscription={isSubscription}
                    modalTitle={modalTitle}
                    localization={props.localization}
                    onKeyUp={handleAddress1KeyUp}
                    onBlur={handleAddress1Blur}
                    onFocus={handleAddress1Focus}
                    onKeyDown={handleAddress1KeyDown}
                    onChange={handleAddress1ChangeDebounced}
                    addressInputRef={comp => (addressInputRef.current = comp)}
                    fireFormErrorAnalytics={fireFormErrorAnalytics}
                    renderAVS={renderAVS}
                />
            </Grid>
            {!isShortenedLoqateForm && (
                <div>
                    <SecondAddressField
                        address2={address.address2}
                        showAddress2Input={showAddress2Input}
                        isAdsRestricted={isAdsRestricted}
                        avsLoqateV2={avsLoqateV2}
                        country={address.country}
                        highlighted={highlightedInputs.has('address2')}
                        isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                        isSubscription={isSubscription}
                        modalTitle={modalTitle}
                        localization={props.localization}
                        onChange={handleAddress2Change}
                        onKeyUp={handleAddress2Change}
                        onAddAddress2Click={handleAddress2SectionClick}
                        address2InputRef={comp => (address2InputRef.current = comp)}
                        fireFormErrorAnalytics={fireFormErrorAnalytics}
                        forceSingleColumn={forceSingleColumn}
                    />
                    <Grid
                        gridTemplateColumns={'1fr 1fr'}
                        gap={GRID_GUTTER}
                    >
                        <PostalCodeField
                            postalCode={address.postalCode}
                            country={address.country}
                            isInternational={isInternational}
                            isAdsRestricted={isAdsRestricted}
                            zipCodeInvalid={zipCodeInvalid}
                            cityStateZipInvalid={cityStateZipInvalid}
                            highlighted={highlightedInputs.has('postalCode')}
                            isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                            isSubscription={isSubscription}
                            modalTitle={modalTitle}
                            localization={props.localization}
                            onChange={handleZipCodeOnChange}
                            onKeyDown={FormValidator.inputAcceptOnlyNumbers}
                            zipCodeInputRef={comp => (zipCodeInputRef.current = comp)}
                            fireFormErrorAnalytics={fireFormErrorAnalytics}
                        />
                        {!displayCityStateInputs && (
                            <Box
                                display='flex'
                                height={forms.HEIGHT}
                                alignItems='center'
                                lineHeight='tight'
                                fontSize={['sm', null]}
                            >
                                {enterZipCodeText}
                            </Box>
                        )}
                    </Grid>
                    <Grid
                        gap={GRID_GUTTER}
                        rowGap={0}
                        gridTemplateColumns={['1fr 1fr', displayCityStateInputs ? '1fr 1fr' : '25% 1fr 1fr']}
                    >
                        {(isInternational || displayCityStateInputs) && (
                            <Box gridRow={['2/2', '1/1']}>
                                {avsLoqateV2 && multiCityOptions.length > 1 ? (
                                    <MultiCityZipDropdown
                                        // GIM-5453: the City slot itself becomes the required chooser when a ZIP
                                        // maps to several cities (Figma single-slot layout). Remount on ZIP change
                                        // so a new multi-city ZIP resets the picker instead of keeping a stale city.
                                        key={address.postalCode}
                                        cities={multiCityOptions}
                                        onSelectCity={handleMultiCitySelect}
                                        label={props.localization?.cityText}
                                    />
                                ) : (
                                    <CityField
                                        city={address.city}
                                        cityStateZipInvalid={cityStateZipInvalid}
                                        highlighted={highlightedInputs.has('city')}
                                        isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                                        isSubscription={isSubscription}
                                        modalTitle={modalTitle}
                                        localization={props.localization}
                                        onChange={handleCityOnChange}
                                        cityInputRef={comp => (cityInputRef.current = comp)}
                                        fireFormErrorAnalytics={fireFormErrorAnalytics}
                                        readOnly={isCityStateLockedByZip}
                                    />
                                )}
                            </Box>
                        )}
                        <Box gridRow={['2/2', '1/1']}>
                            <StateProvinceField
                                state={address.state}
                                country={address.country}
                                stateList={stateList}
                                isInternational={isInternational}
                                displayCityStateInputs={displayCityStateInputs}
                                cityStateZipInvalid={cityStateZipInvalid}
                                highlighted={highlightedInputs.has('state')}
                                isFCFinalReleaseEnabled={isFCFinalReleaseEnabled}
                                isSubscription={isSubscription}
                                modalTitle={modalTitle}
                                localization={props.localization}
                                onChange={handleStateSelect}
                                stateSelectRef={comp => (stateSelectRef.current = comp)}
                                regionInputRef={comp => (regionInputRef.current = comp)}
                                fireFormErrorAnalytics={fireFormErrorAnalytics}
                                readOnly={isCityStateLockedByZip}
                            />
                        </Box>
                    </Grid>
                    {isCountryFieldLast && (
                        <Grid
                            gridTemplateColumns={[null, '1fr 1fr']}
                            gap={GRID_GUTTER}
                        >
                            <CountrySelect
                                country={address.country}
                                countryInvalid={countryInvalid}
                                countryList={props.countryList}
                                isBillingAddress={isBillingAddress}
                                disableCountryList={disableCountryList}
                                localization={props.localization}
                                onChange={handleCountryChange}
                                countrySelectRef={comp => (countrySelectRef.current = comp)}
                                isUS={isUS}
                            />
                        </Grid>
                    )}
                </div>
            )}
        </div>
    );
}

const styles = {
    avsItem: {
        display: 'flex',
        alignItems: 'center',
        padding: space[3],
        cursor: 'pointer',
        '& + *': {
            borderTop: `1px solid ${colors.lightGray}`
        }
    },
    avsItemActive: {
        position: 'relative',
        backgroundColor: colors.nearWhite
    },
    avsItemInner: {
        flex: 1
    },
    avsItemDesc: {
        color: colors.gray
    },
    fullField: {
        [mediaQueries.xsMax]: {
            gridColumn: '1 / -1'
        }
    },
    searchIcon: {
        position: 'absolute',
        top: 13,
        right: space[3],
        pointerEvents: 'none'
    }
};

export default wrapFunctionalComponent(FrictionlessAddressForm, 'FrictionlessAddressForm');
